import { useState } from 'react';
import apiService from '../services/api';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 5000);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      showNotification('يرجى ملء جميع الحقول المطلوبة', 'error');
      return;
    }

    setSubmitting(true);
    try {
      const response = await apiService.submitContactForm(formData);
      showNotification(response.message || 'شكراً لتواصلك معنا! سنرد عليك في أقرب وقت', 'success');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (err) {
      showNotification(err.message || 'حدث خطأ أثناء إرسال الرسالة. يرجى المحاولة مرة أخرى.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: 'fas fa-map-marker-alt',
      title: 'العنوان',
      info: 'الجزائر',
      color: 'from-blue-500 to-indigo-600'
    },
    {
      icon: 'fas fa-envelope',
      title: 'البريد الإلكتروني',
      info: 'info@allemani.dz',
      color: 'from-purple-500 to-pink-600'
    },
    {
      icon: 'fas fa-phone',
      title: 'الهاتف',
      info: '0558501409',
      color: 'from-green-500 to-emerald-600'
    }
  ];

  return (
    <div className="relative z-10 min-h-screen bg-gradient-to-b from-white via-gray-50 to-white overflow-x-hidden">
      {/* Custom Notification */}
      {notification.show && (
        <div className="fixed top-24 left-1/2 -translate-x-1/2 z-[100] animate-fade-in px-4">
          <div className={`max-w-[90vw] sm:min-w-[400px] px-8 py-6 rounded-2xl shadow-2xl font-bold text-white flex items-center gap-4 ${
            notification.type === 'success' 
              ? 'bg-gradient-to-r from-green-500 to-emerald-600' 
              : 'bg-gradient-to-r from-red-500 to-pink-600'
          }`}>
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <i className={`fas ${
                  notification.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'
                } text-3xl`}></i>
              </div>
            </div>
            <div className="flex-1">
              <p className="text-lg leading-relaxed">{notification.message}</p>
            </div>
            <button
              onClick={() => setNotification({ show: false, message: '', type: '' })}
              className="flex-shrink-0 hover:bg-white/20 rounded-full w-8 h-8 flex items-center justify-center transition-colors"
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>
      )}

      {/* Blue gradient blobs */}
      <div className="absolute top-0 right-0 w-64 h-64 md:w-80 md:h-80 lg:w-96 lg:h-96 bg-blue-500/20 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-48 h-48 md:w-64 md:h-64 lg:w-80 lg:h-80 bg-indigo-500/25 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-64 h-64 md:w-80 md:h-80 lg:w-96 lg:h-96 bg-secondary/5 rounded-full blur-3xl"></div>

      <div className="container mx-auto px-4 py-24 relative z-10">
        <div className="max-w-[1200px] mx-auto">
          {/* Header */}
          <div className="text-center mb-20">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full mb-6 font-semibold text-sm">
              <i className="fas fa-envelope"></i>
              <span>اتصل بنا</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-black text-text mb-6 leading-tight">
              نحن هنا <span className="text-primary">لمساعدتك</span>
            </h1>
            <p className="text-xl md:text-2xl text-text2 leading-relaxed max-w-[900px] mx-auto">
              عندك سؤال ولا استفسار؟ تواصل معانا وراح نردو عليك بسرعة
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
            {/* Contact Form */}
            <div className="bg-white rounded-3xl p-6 md:p-8 lg:p-10 shadow-2xl border-2 border-gray-100">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center text-white">
                  <i className="fas fa-paper-plane"></i>
                </div>
                <h2 className="text-3xl font-black text-text">أرسل لنا رسالة</h2>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-text font-bold mb-3 text-lg">الاسم الكامل</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-5 py-4 bg-gray-50 border-2 border-gray-200 rounded-2xl text-text outline-none focus:border-primary focus:bg-white transition-all text-lg"
                    placeholder="دخل اسمك"
                  />
                </div>

                <div>
                  <label className="block text-text font-bold mb-3 text-lg">البريد الإلكتروني</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-5 py-4 bg-gray-50 border-2 border-gray-200 rounded-2xl text-text outline-none focus:border-primary focus:bg-white transition-all text-lg"
                    placeholder="example@email.com"
                  />
                </div>

                <div>
                  <label className="block text-text font-bold mb-3 text-lg">رقم الهاتف (اختياري)</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-5 py-4 bg-gray-50 border-2 border-gray-200 rounded-2xl text-text outline-none focus:border-primary focus:bg-white transition-all text-lg"
                    placeholder="+213 XXX XX XX XX"
                    dir="ltr"
                  />
                </div>

                <div>
                  <label className="block text-text font-bold mb-3 text-lg">الموضوع</label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-5 py-4 bg-gray-50 border-2 border-gray-200 rounded-2xl text-text outline-none focus:border-primary focus:bg-white transition-all text-lg"
                    placeholder="موضوع رسالتك"
                  />
                </div>

                <div>
                  <label className="block text-text font-bold mb-3 text-lg">الرسالة</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="6"
                    className="w-full px-5 py-4 bg-gray-50 border-2 border-gray-200 rounded-2xl text-text outline-none focus:border-primary focus:bg-white transition-all resize-none text-lg"
                    placeholder="اكتب رسالتك هنا..."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="group w-full flex items-center justify-center gap-3 px-8 py-5 bg-gradient-to-r from-primary to-primary-dark text-white font-black rounded-2xl hover:shadow-2xl transition-all text-lg disabled:opacity-50"
                >
                  <span>{submitting ? 'جاري الإرسال...' : 'إرسال الرسالة'}</span>
                  <i className="fas fa-paper-plane group-hover:-translate-y-1 group-hover:translate-x-1 transition-transform"></i>
                </button>
              </form>
            </div>

            {/* Contact Info Cards */}
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-primary via-primary-dark to-secondary rounded-3xl p-6 md:p-8 lg:p-10 shadow-2xl text-white relative overflow-hidden">
                {/* Background pattern */}
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute inset-0" style={{
                    backgroundImage: `radial-gradient(circle at 20% 30%, rgba(255,255,255,0.3) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(255,255,255,0.2) 0%, transparent 50%)`
                  }}></div>
                </div>

                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                      <i className="fas fa-headset text-xl"></i>
                    </div>
                    <h2 className="text-3xl font-black">معلومات التواصل</h2>
                  </div>
                  <p className="text-white/90 text-lg leading-relaxed mb-8">
                    نحن متواجدون دائماً للرد على استفساراتكم ومساعدتكم في أي وقت
                  </p>

                  <div className="space-y-6">
                    {contactInfo.map((item, index) => (
                      <div key={index} className="flex items-start gap-4 bg-white/10 backdrop-blur-sm rounded-2xl p-6 hover:bg-white/20 transition-all flex-row-reverse">
                        <div className={`w-14 h-14 bg-gradient-to-br ${item.color} rounded-xl flex items-center justify-center text-white text-xl flex-shrink-0 shadow-lg`}>
                          <i className={item.icon}></i>
                        </div>
                        <div className="flex-1 text-right">
                          <h3 className="text-lg font-bold mb-1">{item.title}</h3>
                          <p className="text-white/90" dir={item.dir || 'auto'}>{item.info}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-8 pt-8 border-t border-white/20">
                    <h3 className="text-xl font-bold mb-4">تابعنا على</h3>
                    <div className="flex gap-4">
                      <a href="#" className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center hover:bg-white/30 transition-all text-lg">
                        <i className="fab fa-facebook"></i>
                      </a>
                      <a href="#" className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center hover:bg-white/30 transition-all text-lg">
                        <i className="fab fa-instagram"></i>
                      </a>
                      <a href="#" className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center hover:bg-white/30 transition-all text-lg">
                        <i className="fab fa-linkedin"></i>
                      </a>
                      <a href="#" className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center hover:bg-white/30 transition-all text-lg">
                        <i className="fab fa-youtube"></i>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* FAQ Quick Links */}
          <div className="bg-white rounded-3xl p-6 md:p-8 lg:p-10 shadow-xl border-2 border-gray-100">
            <h2 className="text-3xl font-black text-text text-center mb-8">
              أسئلة شائعة قد تساعدك
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <a href="/pricing" className="group flex items-center gap-4 p-6 bg-gray-50 rounded-2xl hover:bg-primary/5 transition-all border-2 border-gray-100 hover:border-primary/30">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center text-white flex-shrink-0 group-hover:scale-110 transition-transform">
                  <i className="fas fa-dollar-sign"></i>
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-text mb-1">الأسعار</h3>
                  <p className="text-sm text-text3">شوف خطط الأسعار</p>
                </div>
                <i className="fas fa-arrow-left text-primary group-hover:-translate-x-1 transition-transform"></i>
              </a>

              <a href="/about" className="group flex items-center gap-4 p-6 bg-gray-50 rounded-2xl hover:bg-primary/5 transition-all border-2 border-gray-100 hover:border-primary/30">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center text-white flex-shrink-0 group-hover:scale-110 transition-transform">
                  <i className="fas fa-info-circle"></i>
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-text mb-1">من نحن</h3>
                  <p className="text-sm text-text3">تعرف علينا أكثر</p>
                </div>
                <i className="fas fa-arrow-left text-primary group-hover:-translate-x-1 transition-transform"></i>
              </a>

              <a href="/courses" className="group flex items-center gap-4 p-6 bg-gray-50 rounded-2xl hover:bg-primary/5 transition-all border-2 border-gray-100 hover:border-primary/30">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center text-white flex-shrink-0 group-hover:scale-110 transition-transform">
                  <i className="fas fa-book"></i>
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-text mb-1">الدورات</h3>
                  <p className="text-sm text-text3">تصفح الدورات</p>
                </div>
                <i className="fas fa-arrow-left text-primary group-hover:-translate-x-1 transition-transform"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
